import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import { authApi } from "../../api/auth";
import { useAuth } from "../../context/AuthContext";
import {
  AUTH_BRAND,
  AuthPasswordField,
  authPrimaryButtonSx,
} from "../../components/dashboard/auth/AuthFormShell";

export default function AccountSecurityPage() {
  const { user } = useAuth();
  const [form, setForm] = React.useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const handleChange = (event) => {
    setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.currentPassword) {
      setError("Ingresá tu contraseña actual.");
      return;
    }
    if (form.newPassword.length < 8) {
      setError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError("Las contraseñas nuevas no coinciden.");
      return;
    }
    if (form.currentPassword === form.newPassword) {
      setError("La nueva contraseña debe ser diferente de la actual.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setMessage("");
      const data = await authApi.changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setMessage(data?.message || "Contraseña cambiada correctamente.");
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setError(err.message || "No se pudo cambiar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" disableGutters>
      <Stack spacing={{ xs: 1.5, sm: 2.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: { xs: 40, sm: 46 },
                height: { xs: 40, sm: 46 },
                flex: "0 0 auto",
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                color: AUTH_BRAND.orange,
                bgcolor: "rgba(243,90,58,.1)",
              }}
            >
              <SecurityRoundedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" sx={{ color: AUTH_BRAND.dark, fontWeight: 950 }}>
                Seguridad
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", overflowWrap: "anywhere" }}>
                {user?.email || "Tu cuenta"}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Card elevation={0} sx={{ borderRadius: { xs: 3, sm: 4 }, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={1.75}>
              <Box>
                <Typography variant="h6" sx={{ color: AUTH_BRAND.dark, fontWeight: 950 }}>
                  Cambiar contraseña
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", mt: 0.35 }}>
                  Las demás sesiones abiertas se cerrarán por seguridad.
                </Typography>
              </Box>

              {message ? <Alert severity="success">{message}</Alert> : null}
              {error ? <Alert severity="error">{error}</Alert> : null}

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={1.5}>
                  <AuthPasswordField
                    label="Contraseña actual"
                    name="currentPassword"
                    value={form.currentPassword}
                    onChange={handleChange}
                    autoComplete="current-password"
                    disabled={loading}
                    inputProps={{ minLength: undefined }}
                  />
                  <AuthPasswordField
                    label="Nueva contraseña"
                    name="newPassword"
                    value={form.newPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                    disabled={loading}
                    helperText="Mínimo 8 caracteres y distinta de la actual."
                  />
                  <AuthPasswordField
                    label="Repetir contraseña"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                    disabled={loading}
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    variant="contained"
                    size="large"
                    sx={{ ...authPrimaryButtonSx, width: { xs: "100%", sm: "auto" }, alignSelf: { sm: "flex-start" } }}
                  >
                    {loading ? "Guardando..." : "Cambiar contraseña"}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}
